import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useDemandes } from '../hooks/useDemandes';
import { useDelai, useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Chargement, EnTetePage, Erreur, Modale, Tableau, type Colonne } from '../components/Elements';
import { BandeauMetriques } from '../components/BandeauMetriques';
import { BarreRecherche, type FiltreActif } from '../components/BarreRecherche';
import { dateExport, exporterTableau } from '../lib/export';
import { formaterDate, formaterDateHeure } from '../lib/format';
import type { Demande, PersonneDetail, StatutDemande, SyntheseDemandes } from '../types';
import { formaterTelephone } from '../lib/telephone';

/**
 * Les demandes recues par le formulaire public (2026-09-24).
 *
 * Meme grammaire que Adherents, Inscriptions et Paiements : quatre
 * tuiles, la barre de recherche et ses filtres, la liste. Rien n'entre
 * dans le fichier des adherents avant le clic « Creer l'adherent et
 * l'inscription » du detail — ou « Rattacher et inscrire », quand le
 * rapprochement a trouve une fiche et que le secretariat l'a choisie.
 */

const STATUTS: Record<StatutDemande, { libelle: string; ton: 'contour' | 'succes' | 'danger' | 'neutre' }> = {
  recue: { libelle: 'reçue', ton: 'contour' },
  traitee: { libelle: 'traitée', ton: 'succes' },
  refusee: { libelle: 'refusée', ton: 'danger' },
  expiree: { libelle: 'expirée', ton: 'neutre' },
};

const PUPITRES: Record<string, string> = { S: 'Soprano', A: 'Alto', T: 'Ténor', B: 'Basse' };

const SEPT_JOURS_MS = 7 * 24 * 3_600_000;

type DecisionChamp = 'garder' | 'remplacer';
type DecisionAdresse = 'ignorer' | 'ajouter' | 'remplacer';
type DecisionCommentaire = 'ignorer' | 'ajouter';
type ChampFiche = 'email' | 'telephone' | 'dateNaissance' | 'civilite';
type DecisionsFiche = Partial<Record<ChampFiche, DecisionChamp>>;

const sansEspaces = (v: string) => v.replace(/\s+/g, ' ').trim().toLowerCase();

/** Les decisions d'un rattachement, portees par la modale et envoyees telles quelles. */
interface Decisions {
  fiche: DecisionsFiche;
  adresse: DecisionAdresse;
  commentaire: DecisionCommentaire;
}
const DECISIONS_DEFAUT: Decisions = { fiche: {}, adresse: 'ajouter', commentaire: 'ajouter' };

/** La contribution, telle qu'elle ira sur la fiche. */
const texteContribution = (contribution: string | null) =>
  contribution?.trim() ? `Contribution à la vie de la chorale : ${contribution.trim()}` : null;

/**
 * LE TABLEAU DE LA DEMANDE (maquette v2, 2026-10-07) : tout ce qui a ete
 * saisi en ligne, en une colonne, et — quand la demande est RATTACHEE a
 * une fiche — la valeur de la fiche et la decision, champ par champ.
 *
 * Deux groupes. « Identite et coordonnees » se compare a la fiche : un
 * champ identique ne demande rien, un champ vide propose de le completer
 * (le comportement d'avant), un champ different propose de garder la
 * fiche — on n'ecrase jamais sans un geste. L'adresse a trois issues
 * parce qu'une fiche peut en porter plusieurs ; la contribution s'ajoute
 * au commentaire de la fiche, ou s'ignore. « Inscription » se lit sans
 * decision : creneau, pupitre, adhesion, facture, charte s'appliquent
 * tels que saisis.
 *
 * Sans fiche (nouvelle fiche, ou demande deja traitee), seules les
 * valeurs saisies s'affichent : tout est repris de la saisie.
 *
 * La fiche se charge ici : le rapprochement ne porte ni la civilite, ni
 * les adresses, ni le commentaire, et la comparaison a besoin des trois.
 */
function TableauDemande({
  demande,
  personneId,
  decisions,
  onDecisions,
}: {
  demande: Demande;
  /** La fiche rattachee ; vide pour une nouvelle fiche ou une simple lecture. */
  personneId: string;
  decisions: Decisions;
  onDecisions: (d: Decisions) => void;
}) {
  // La fiche complete porte deja ses adresses (2026-10-09). L'ecran les
  // demandait a GET /api/personnes/:id/adresses, qui n'existe qu'en POST :
  // 404 silencieux, aucune adresse de fiche jamais comparee, et une
  // adresse identique proposee en doublon a chaque acceptation.
  const fiche = useRessource(
    () => (personneId ? api.get<PersonneDetail>(`/api/personnes/${personneId}`) : Promise.resolve(null)),
    [personneId],
  );
  const s = demande.saisie;
  const rattache = personneId !== '';
  // `useRessource` garde la fiche precedente pendant le rechargement : on
  // n'affiche que celle qui correspond au choix.
  const p = rattache && fiche.donnees && fiche.donnees.id === personneId ? fiche.donnees : null;
  const pret = !rattache || p !== null;

  type Etat = 'identique' | 'vide' | 'diff' | 'saisie';
  type Ligne = { champ: ChampFiche | 'nom'; libelle: string; surFiche: string | null; saisi: string | null; etat: Etat };
  const lignes: Ligne[] = [];
  const poser = (champ: Ligne['champ'], libelle: string, surFiche: string | null, saisi: string | null, egal: boolean) => {
    if (!saisi) return; // rien de saisi : rien a montrer ni a decider
    lignes.push({ champ, libelle, surFiche, saisi, etat: !p ? 'saisie' : egal ? 'identique' : surFiche ? 'diff' : 'vide' });
  };
  const nomSaisi = `${demande.nom.toUpperCase()} ${demande.prenom}`;
  poser('nom', 'Nom, prénom', p ? `${p.nom.toUpperCase()} ${p.prenom}` : null, nomSaisi, !!p && sansEspaces(`${p.nom} ${p.prenom}`) === sansEspaces(`${demande.nom} ${demande.prenom}`));
  poser(
    'civilite',
    'Civilité',
    p?.civilite ? (p.civilite === 'M' ? 'M.' : 'Mme') : null,
    s.civilite ? (s.civilite === 'M' ? 'M.' : 'Mme') : null,
    !!p?.civilite && p.civilite === s.civilite,
  );
  poser(
    'dateNaissance',
    'Date de naissance',
    p?.date_naissance ? formaterDate(p.date_naissance) : null,
    demande.date_naissance ? formaterDate(demande.date_naissance) : null,
    !!p?.date_naissance && p.date_naissance === demande.date_naissance,
  );
  poser('email', 'Email', p?.email ?? null, demande.email, !!p?.email && p.email.toLowerCase() === demande.email.toLowerCase());
  poser(
    'telephone',
    'Téléphone',
    p?.telephone ? formaterTelephone(p.telephone, p.telephone_pays) : null,
    formaterTelephone(demande.telephone, demande.telephone_pays),
    !!p?.telephone && p.telephone.replace(/\D/g, '') === demande.telephone.replace(/\D/g, ''),
  );

  // L'adresse : identique si la fiche porte deja la meme (voie, CP, ville).
  const adresseSaisie = s.adresse.voie
    ? [s.adresse.voie, [s.adresse.codePostal, s.adresse.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : null;
  const existantes = p?.adresses ?? [];
  const primaire = existantes.find((a) => a.rang === 'primaire') ?? existantes[0] ?? null;
  const adresseIdentique =
    !!p &&
    !!adresseSaisie &&
    existantes.some(
      (a) =>
        sansEspaces(a.ligne1) === sansEspaces(s.adresse.voie ?? '') &&
        (a.code_postal ?? '') === (s.adresse.codePostal ?? '') &&
        sansEspaces(a.ville ?? '') === sansEspaces(s.adresse.ville ?? ''),
    );
  const contribution = texteContribution(s.contribution);
  const commentaireDejaLa = !!p && !!contribution && (p.commentaire ?? '').includes(contribution);

  const decisionDe = (l: Ligne): DecisionChamp =>
    l.champ === 'nom' ? 'garder' : (decisions.fiche[l.champ] ?? (l.etat === 'vide' ? 'remplacer' : 'garder'));
  const poserChamp = (champ: ChampFiche, d: DecisionChamp) => onDecisions({ ...decisions, fiche: { ...decisions.fiche, [champ]: d } });

  // La phrase de resume : ce qui va se passer, en clair, avant le clic.
  const effets: string[] = [];
  if (p) {
    for (const l of lignes) {
      if (l.etat === 'identique' || l.champ === 'nom') continue;
      const remplace = decisionDe(l) === 'remplacer';
      const nom = l.libelle.toLowerCase();
      effets.push(l.etat === 'vide' ? (remplace ? `${nom} complété` : `${nom} laissé vide`) : remplace ? `${nom} remplacé` : `${nom} conservé`);
    }
    if (adresseSaisie && !adresseIdentique) {
      effets.push(
        decisions.adresse === 'ignorer'
          ? 'adresse saisie ignorée'
          : decisions.adresse === 'ajouter' || !primaire
            ? primaire
              ? 'adresse saisie ajoutée en secondaire'
              : 'adresse saisie ajoutée'
            : 'adresse saisie en primaire, l’ancienne en secondaire',
      );
    }
    if (contribution && !commentaireDejaLa) {
      effets.push(decisions.commentaire === 'ajouter' ? 'contribution ajoutée au commentaire' : 'contribution ignorée');
    }
  }
  const suite = `l’inscription à ${demande.creneau_libelle}${demande.adhesion ? ' et l’adhésion' : ''}`;
  const resume = !p
    ? rattache
      ? ''
      : `Une nouvelle fiche ${nomSaisi} est créée avec ces coordonnées${adresseSaisie ? ', cette adresse' : ''}${contribution ? ' et ce commentaire' : ''}, puis ${suite}.`
    : effets.length > 0
      ? `${effets.join(', ').replace(/^./, (c) => c.toUpperCase())}. Puis ${suite}, selon le formulaire.`
      : `La fiche est déjà conforme à la saisie : rien n’y change. ${suite.replace(/^./, (c) => c.toUpperCase())}, selon le formulaire.`;

  // LA COULEUR DIT CE QUI SERA GARDE (2026-10-07) : la valeur retenue en
  // vert, l'ecartee en gris — la decision se lit sans relire les boutons.
  // Sans decision a prendre (identique, nouvelle fiche), rien n'est teinte.
  const RETENU = 'font-medium text-emerald-700';
  const ECARTE = 'text-encre-400';
  const teinte = (retenu: boolean | null) => (retenu === null ? '' : retenu ? RETENU : ECARTE);
  /** Pour un champ : la fiche est retenue sur « garder », la saisie sur « remplacer ». */
  const teinteChamp = (l: Ligne, cote: 'fiche' | 'saisie') => {
    if (!rattache || l.etat === 'identique' || l.etat === 'saisie' || l.champ === 'nom') return '';
    const remplace = decisionDe(l) === 'remplacer';
    // Une fiche VIDE n'a rien a retenir : seule la saisie se teinte.
    if (cote === 'fiche') return l.etat === 'vide' ? '' : teinte(!remplace);
    return teinte(remplace);
  };
  const adresseADecider = rattache && !!adresseSaisie && !adresseIdentique;
  const teinteAdresse = (cote: 'fiche' | 'saisie') => {
    if (!adresseADecider) return '';
    if (cote === 'saisie') return teinte(decisions.adresse !== 'ignorer');
    if (!primaire) return '';
    // La fiche garde son adresse sauf si la saisie la remplace en primaire.
    return teinte(decisions.adresse !== 'remplacer');
  };
  const commentaireADecider = rattache && !!contribution && !commentaireDejaLa;
  const teinteCommentaire = (cote: 'fiche' | 'saisie') => {
    if (!commentaireADecider) return '';
    if (cote === 'saisie') return teinte(decisions.commentaire === 'ajouter');
    // Le commentaire existant survit dans les deux cas : « ajouter » s'ajoute a la suite.
    return p?.commentaire ? RETENU : '';
  };

  const radio = (nom: string, valeur: string, coche: boolean, onChange: () => void, libelle: string) => (
    <label className="inline-flex items-center gap-1 whitespace-nowrap">
      <input type="radio" name={nom} value={valeur} checked={coche} onChange={onChange} />
      {libelle}
    </label>
  );
  const groupe = (titre: string) => (
    <tr className="bg-encre-50">
      <td colSpan={rattache ? 4 : 2} className="px-2 py-1 text-[11px] uppercase tracking-wide text-encre-500">
        {titre}
      </td>
    </tr>
  );
  const info = (libelle: string, valeur: React.ReactNode, mention?: string) => (
    <tr className="border-t border-encre-100 align-top">
      <td className="whitespace-nowrap px-2 py-1 text-encre-500">{libelle}</td>
      {rattache && <td className="px-2 py-1 text-encre-300">—</td>}
      <td className="px-2 py-1">{valeur}</td>
      {rattache && <td className="px-2 py-1 text-encre-400">{mention ?? ''}</td>}
    </tr>
  );

  return (
    <div className="space-y-2">
      <table className="w-full overflow-hidden rounded-md border border-encre-200 bg-white text-xs">
        <thead>
          <tr className="bg-encre-100 text-left text-[11px] uppercase tracking-wide text-encre-500">
            <th className="w-36 px-2 py-1">Champ</th>
            {rattache && <th className="w-52 px-2 py-1">Sur la fiche</th>}
            <th className="px-2 py-1">Saisi en ligne</th>
            {rattache && <th className="px-2 py-1">Décision</th>}
          </tr>
        </thead>
        <tbody>
          {groupe('Identité et coordonnées')}
          {rattache && !pret ? (
            <tr>
              <td colSpan={4} className="px-2 py-2 text-encre-400">
                Lecture de la fiche…
              </td>
            </tr>
          ) : (
            <>
              {lignes.map((l) => (
                <tr key={l.champ} className="border-t border-encre-100 align-top">
                  <td className="whitespace-nowrap px-2 py-1 text-encre-500">{l.libelle}</td>
                  {rattache && (
                    <td className={`px-2 py-1 ${teinteChamp(l, 'fiche')}`}>
                      {l.surFiche ?? <span className="italic text-encre-400">vide</span>}
                    </td>
                  )}
                  <td className={`px-2 py-1 ${teinteChamp(l, 'saisie')}`}>{l.saisi}</td>
                  {rattache && (
                    <td className="px-2 py-1">
                      {l.etat === 'identique' ? (
                        <span className="text-encre-400">identique</span>
                      ) : l.champ === 'nom' ? (
                        <span className="text-encre-400">la fiche fait foi</span>
                      ) : (
                        <span className="flex flex-wrap gap-2">
                          {radio(`decision-${l.champ}`, 'garder', decisionDe(l) === 'garder', () => poserChamp(l.champ as ChampFiche, 'garder'), l.etat === 'vide' ? 'Laisser vide' : 'Garder')}
                          {radio(`decision-${l.champ}`, 'remplacer', decisionDe(l) === 'remplacer', () => poserChamp(l.champ as ChampFiche, 'remplacer'), l.etat === 'vide' ? 'Compléter' : 'Remplacer')}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {/* La ligne s'affiche des que l'un des deux cotes porte une
                  adresse (2026-10-09). Masquee quand la famille n'avait rien
                  saisi, elle cachait aussi celle de la fiche — et faisait
                  croire que l'adresse n'etait pas remontee (SCHULZ). */}
              {(adresseSaisie || (rattache && primaire)) && (
                <tr className="border-t border-encre-100 align-top">
                  <td className="whitespace-nowrap px-2 py-1 text-encre-500">Adresse</td>
                  {rattache && (
                    <td className={`px-2 py-1 ${teinteAdresse('fiche')}`}>
                      {primaire ? (
                        <>
                          {[primaire.ligne1, [primaire.code_postal, primaire.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                          <span className="block text-[11px] font-normal text-encre-400">
                            {adresseADecider && decisions.adresse === 'remplacer' ? 'passera en secondaire' : primaire.rang}
                          </span>
                        </>
                      ) : (
                        <span className="italic text-encre-400">aucune</span>
                      )}
                    </td>
                  )}
                  <td className={`px-2 py-1 ${teinteAdresse('saisie')}`}>
                    {adresseSaisie ?? <span className="italic text-encre-400">non saisie</span>}
                  </td>
                  {rattache && (
                    <td className="px-2 py-1">
                      {!adresseSaisie ? (
                        <span className="text-encre-400">la fiche fait foi</span>
                      ) : adresseIdentique ? (
                        <span className="text-encre-400">identique</span>
                      ) : (
                        <span className="flex flex-wrap gap-2">
                          {radio('decision-adresse', 'ignorer', decisions.adresse === 'ignorer', () => onDecisions({ ...decisions, adresse: 'ignorer' }), 'Ignorer')}
                          {radio('decision-adresse', 'ajouter', decisions.adresse === 'ajouter', () => onDecisions({ ...decisions, adresse: 'ajouter' }), primaire ? 'Ajouter en secondaire' : 'Ajouter')}
                          {primaire && radio('decision-adresse', 'remplacer', decisions.adresse === 'remplacer', () => onDecisions({ ...decisions, adresse: 'remplacer' }), 'Remplacer la primaire')}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              )}
              {contribution && (
                <tr className="border-t border-encre-100 align-top">
                  <td className="whitespace-nowrap px-2 py-1 text-encre-500">Commentaire</td>
                  {rattache && (
                    <td className={`px-2 py-1 whitespace-pre-line ${teinteCommentaire('fiche')}`}>
                      {p?.commentaire ? p.commentaire : <span className="italic text-encre-400">vide</span>}
                    </td>
                  )}
                  <td className={`px-2 py-1 ${teinteCommentaire('saisie')}`}>{contribution}</td>
                  {rattache && (
                    <td className="px-2 py-1">
                      {commentaireDejaLa ? (
                        <span className="text-encre-400">déjà sur la fiche</span>
                      ) : (
                        <span className="flex flex-wrap gap-2">
                          {radio('decision-commentaire', 'ignorer', decisions.commentaire === 'ignorer', () => onDecisions({ ...decisions, commentaire: 'ignorer' }), 'Ignorer')}
                          {radio('decision-commentaire', 'ajouter', decisions.commentaire === 'ajouter', () => onDecisions({ ...decisions, commentaire: 'ajouter' }), p?.commentaire ? 'Ajouter à la suite' : 'Ajouter')}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              )}
            </>
          )}

          {groupe('Inscription')}
          {info('Créneau', demande.creneau_libelle, 'inscription créée au tarif de l’activité')}
          {demande.pupitre && info('Pupitre', PUPITRES[demande.pupitre] ?? demande.pupitre, 'sur l’inscription')}
          {demande.instrument && info('Instrument', demande.instrument, 'sur l’inscription')}
          {info('Adhésion', demande.adhesion ? 'oui' : 'non', demande.adhesion ? 'adhésion posée, selon le formulaire' : 'pas d’adhésion')}
          {info('Facture', s.facture ? `demandée${s.facturePrecisions ? ` · ${s.facturePrecisions}` : ''}` : 'non demandée')}
          {info('Charte', `acceptée le ${formaterDateHeure(demande.charte_acceptee_le)}`)}
        </tbody>
      </table>
      {resume && demande.statut === 'recue' && (
        <p className="border-l-2 border-violet-500 bg-encre-50 px-2.5 py-1.5 text-xs text-encre-700">{resume}</p>
      )}
    </div>
  );
}

function libelleRapprochement(demande: Demande): { texte: string; alerte: boolean } {
  const r = demande.rapprochement;
  if (!r) return { texte: '', alerte: false };
  switch (r.niveau) {
    case 'connu': {
      const annees = r.personnes[0]?.annees ?? [];
      return {
        texte: annees.length ? `connu, inscrit ${annees[annees.length - 1]}` : 'connu',
        alerte: true,
      };
    }
    case 'probable':
      return { texte: 'probable : même nom, même prénom', alerte: true };
    case 'famille':
      return { texte: 'famille connue', alerte: true };
    default:
      return { texte: 'nouvelle personne', alerte: false };
  }
}

export function DemandesPage() {
  const { peut, entiteActive } = useAuth();
  const cle = entiteActive?.id ?? '';
  const compteur = useDemandes();
  const [recherche, setRecherche] = useState('');
  const rechercheDifferee = useDelai(recherche);
  const [statut, setStatut] = useState<StatutDemande | 'toutes'>('recue');
  const [creneauRef, setCreneauRef] = useState('');
  const [seulementLongues, setSeulementLongues] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);

  const synthese = useRessource(() => api.get<SyntheseDemandes>('/api/demandes/synthese'), [cle]);
  const demandes = useRessource(
    () =>
      api.get<Demande[]>('/api/demandes', {
        statut,
        recherche: rechercheDifferee || undefined,
        creneauRef: creneauRef || undefined,
      }),
    [cle, statut, rechercheDifferee, creneauRef],
  );

  const recharger = () => {
    synthese.recharger();
    demandes.recharger();
    compteur.rafraichir();
  };

  const maintenant = Date.now();
  const liste = (demandes.donnees ?? []).filter(
    (d) => !seulementLongues || maintenant - new Date(d.created_at).getTime() > SEPT_JOURS_MS,
  );
  const ouverte = liste.find((d) => d.id === detail) ?? null;

  const filtresActifs: FiltreActif[] = [];
  if (statut !== 'toutes') {
    filtresActifs.push({ cle: 'statut', libelle: STATUTS[statut].libelle, onRetirer: () => setStatut('toutes') });
  }
  if (creneauRef) {
    const c = synthese.donnees?.creneaux.find((x) => x.ref === creneauRef);
    filtresActifs.push({ cle: 'creneau', libelle: c?.libelle ?? 'créneau', onRetirer: () => setCreneauRef('') });
  }
  if (seulementLongues) {
    filtresActifs.push({ cle: 'longues', libelle: 'en attente depuis plus de 7 jours', onRetirer: () => setSeulementLongues(false) });
  }

  const colonnes: Colonne<Demande>[] = [
    { cle: 'numero', entete: 'N°', classe: 'tabulaire whitespace-nowrap', rendu: (d) => d.numero },
    { cle: 'date', entete: 'Reçue le', classe: 'tabulaire whitespace-nowrap', rendu: (d) => formaterDate(d.created_at) },
    {
      cle: 'choriste',
      entete: 'Choriste',
      rendu: (d) => (
        <span>
          <span className="font-medium">
            {d.nom.toUpperCase()} {d.prenom}
          </span>
          <br />
          <span className="text-xs text-encre-500">
            {d.email} · {formaterTelephone(d.telephone, d.telephone_pays)}
          </span>
        </span>
      ),
    },
    { cle: 'creneau', entete: 'Créneau', rendu: (d) => d.creneau_libelle },
    {
      cle: 'pupitre',
      entete: 'Pupitre',
      rendu: (d) => (d.pupitre ? PUPITRES[d.pupitre] ?? d.pupitre : d.instrument ? <span className="text-xs">{d.instrument}</span> : <span className="text-encre-300">—</span>),
    },
    { cle: 'adhesion', entete: 'Adhésion', rendu: (d) => (d.adhesion ? 'oui' : 'non') },
    {
      cle: 'rapprochement',
      entete: 'Déjà connu ?',
      rendu: (d) => {
        const r = libelleRapprochement(d);
        return <span className={`text-xs ${r.alerte ? 'text-amber-800' : 'text-encre-500'}`}>{r.texte}</span>;
      },
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (d) => <Badge ton={STATUTS[d.statut].ton}>{STATUTS[d.statut].libelle}</Badge>,
    },
  ];

  const s = synthese.donnees;

  return (
    <>
      <EnTetePage
        titre="Inscriptions en ligne"
        description="Les demandes reçues par le formulaire public. Rien n’entre dans le fichier des adhérents tant qu’une demande n’est pas acceptée ici. Cliquez une ligne pour la lire et la traiter."
      />

      {s && (
        <BandeauMetriques
          metriques={[
            { libelle: 'Reçues', valeur: String(s.recues), detail: 'à traiter' },
            { libelle: 'Traitées', valeur: String(s.traitees) },
            { libelle: 'Refusées', valeur: String(s.refusees) },
            {
              libelle: 'En attente depuis plus de 7 jours',
              valeur: String(s.en_attente_longue),
              ton: 'alerte',
              action: () => {
                setStatut('recue');
                setSeulementLongues(true);
              },
            },
          ]}
        />
      )}

      <BarreRecherche
        recherche={recherche}
        onChercher={setRecherche}
        placeholder="Nom, prénom, email, numéro de demande…"
        filtres={
          <>
            <select className="champ w-44" value={creneauRef} onChange={(e) => setCreneauRef(e.target.value)}>
              <option value="">Créneau</option>
              {(s?.creneaux ?? []).map((c) => (
                <option key={c.ref} value={c.ref}>
                  {c.libelle}
                </option>
              ))}
            </select>
            <select className="champ w-44" value={statut} onChange={(e) => setStatut(e.target.value as StatutDemande | 'toutes')}>
              <option value="recue">Reçue</option>
              <option value="toutes">Toutes</option>
              <option value="traitee">Traitée</option>
              <option value="refusee">Refusée</option>
              <option value="expiree">Expirée</option>
            </select>
          </>
        }
        actifs={filtresActifs}
        onToutEffacer={() => {
          setStatut('toutes');
          setCreneauRef('');
          setSeulementLongues(false);
        }}
        resume={`${liste.length} demande(s)`}
        onExporter={
          liste.length
            ? () =>
                exporterTableau(
                  'demandes-inscription',
                  ['Numéro', 'Reçue le', 'Nom', 'Prénom', 'Naissance', 'Email', 'Téléphone', 'Adresse', 'Créneau', 'Pupitre', 'Instrument', 'Adhésion', 'Facture', 'Statut'],
                  liste.map((d) => [
                    d.numero,
                    dateExport(d.created_at),
                    d.nom,
                    d.prenom,
                    dateExport(d.date_naissance),
                    d.email,
                    formaterTelephone(d.telephone, d.telephone_pays),
                    [d.saisie.adresse.voie, [d.saisie.adresse.codePostal, d.saisie.adresse.ville].filter(Boolean).join(' ')].filter(Boolean).join(', '),
                    d.creneau_libelle,
                    d.pupitre ? PUPITRES[d.pupitre] ?? d.pupitre : '',
                    d.instrument ?? '',
                    d.adhesion ? 'oui' : 'non',
                    d.saisie.facture ? 'oui' : 'non',
                    STATUTS[d.statut].libelle,
                  ]),
                )
            : undefined
        }
      />

      {demandes.erreur && <Erreur message={demandes.erreur} onReessayer={demandes.recharger} />}
      {demandes.chargement && !demandes.donnees ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={liste}
            cleDe={(d) => d.id}
            messageVide="Aucune demande."
            onLigneCliquee={(d) => setDetail(d.id)}
          />
        </div>
      )}

      <p className="mt-2 text-xs text-encre-400">
        Les demandes refusées et expirées sont effacées après six mois. La fenêtre d’ouverture du
        formulaire se règle sur la fiche de l’école.
      </p>

      {ouverte && (
        <DetailDemande
          demande={ouverte}
          peutTraiter={peut('demande.traiter')}
          onFermer={() => setDetail(null)}
          onChange={recharger}
        />
      )}
    </>
  );
}

function DetailDemande({
  demande,
  peutTraiter,
  onFermer,
  onChange,
}: {
  demande: Demande;
  peutTraiter: boolean;
  onFermer: () => void;
  onChange: () => void;
}) {
  const [note, setNote] = useState(demande.note ?? '');
  const [motif, setMotif] = useState('');
  const [refusEnCours, setRefusEnCours] = useState(false);
  // Le choix de l'acceptation : creer une fiche, ou rattacher a l'une de
  // celles que le rapprochement propose. Et la cotisation, pre-posee sur
  // ce que la personne a demande.
  // Un adherent CONNU ou PROBABLE (meme nom et prenom) est pre-selectionne
  // (2026-10-07) : neuf fois sur dix c'est lui, et creer un doublon est la
  // faute a eviter — le tableau montre de toute facon ce qui differe.
  const [personneChoisie, setPersonneChoisie] = useState<string>(
    demande.rapprochement && ['connu', 'probable'].includes(demande.rapprochement.niveau)
      ? (demande.rapprochement.personnes[0]?.id ?? '')
      : '',
  );
  // Les decisions champ par champ sur une fiche rattachee (2026-10-07) ;
  // vides, le serveur applique son defaut — completer ce qui manque,
  // ajouter l'adresse et la contribution. L'adhesion suit le formulaire.
  const [decisions, setDecisions] = useState<Decisions>(DECISIONS_DEFAUT);
  // L'inscription creee vit dans l'ANNEE DE LA DEMANDE — celle en
  // preparation, le plus souvent —, pas dans celle du bandeau. Ouvrir la
  // fiche sans y basculer montrait une fiche sans inscription, et le
  // secretariat concluait que l'inscription n'avait pas suivi
  // (2026-09-24). Le lien bascule donc le bandeau avant de naviguer.
  const annee = useAnnee();
  const anneeDeLaDemande = annee.annees.find((a) => a.libelle === demande.annee_libelle) ?? null;
  const basculerSurLAnnee = () => {
    if (anneeDeLaDemande && anneeDeLaDemande.id !== annee.anneeId) annee.choisir(anneeDeLaDemande.id);
  };
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const r = demande.rapprochement;
  const libelle = libelleRapprochement(demande);

  const changerStatut = async (statut: 'traitee' | 'refusee' | 'recue') => {
    setErreur(null);
    setEnCours(true);
    try {
      if (note !== (demande.note ?? '')) {
        await api.patch(`/api/demandes/${demande.id}/note`, { note: note || null });
      }
      await api.patch(`/api/demandes/${demande.id}/statut`, {
        statut,
        motif: statut === 'refusee' ? motif : null,
      });
      onChange();
      onFermer();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const accepter = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      if (note !== (demande.note ?? '')) {
        await api.patch(`/api/demandes/${demande.id}/note`, { note: note || null });
      }
      await api.post(`/api/demandes/${demande.id}/acceptation`, {
        personneId: personneChoisie || null,
        ...(personneChoisie
          ? { fiche: { ...decisions.fiche, commentaire: decisions.commentaire }, adresse: decisions.adresse }
          : {}),
      });
      onChange();
      onFermer();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Acceptation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const enregistrerNote = async () => {
    setErreur(null);
    try {
      await api.patch(`/api/demandes/${demande.id}/note`, { note: note || null });
      onChange();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    }
  };

  return (
    <Modale
      titre={
        <>
          Demande {demande.numero} <Badge ton={STATUTS[demande.statut].ton}>{STATUTS[demande.statut].libelle}</Badge>
          <span className="text-sm font-normal text-encre-500">
            le {formaterDateHeure(demande.created_at)} · {demande.creneau_libelle}
          </span>
        </>
      }
      titreAccessible={`Demande ${demande.numero}`}
      ouverte
      onFermer={onFermer}
      large
    >
      {/* UNE COLONNE (maquette v2, 2026-10-07) : le tableau porte tout ce
          qui a ete saisi, et la decision quand la demande se rattache. */}
      <div className="space-y-4">
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-encre-500">Rapprochement</h3>
          {r && r.niveau !== 'nouveau' ? (
            <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <strong className="block">{libelle.texte}</strong>
              {r.personnes.map((p) => (
                <div key={p.id}>
                  {p.nom.toUpperCase()} {p.prenom}
                  {p.date_naissance ? `, née le ${formaterDate(p.date_naissance)}` : ''}
                  {p.annees.length ? `, inscrit en ${p.annees.join(', ')}` : ', sans inscription'}
                  {p.email && p.email.toLowerCase() !== demande.email.toLowerCase() ? ` · email différent : ${p.email}` : ''}{' '}
                  <Link to={`/adherents/${p.id}`} className="font-medium underline" onClick={basculerSurLAnnee}>
                    Ouvrir la fiche
                  </Link>
                </div>
              ))}
              {r.contacts.map((c) => (
                <div key={c.id}>
                  Contact {[c.prenom, c.nom].filter(Boolean).join(' ') || 'sans nom'}
                  {c.personnes.length ? `, rattaché à ${c.personnes.join(', ')}` : ''}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-encre-500">Aucune fiche ne porte ce nom ni ces coordonnées : nouvelle personne.</p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-encre-500">Traitement</h3>
          {demande.statut !== 'recue' && (
            <p className="text-sm text-encre-600">
              {STATUTS[demande.statut].libelle}
              {demande.traitee_le ? ` le ${formaterDateHeure(demande.traitee_le)}` : ''}
              {demande.traitee_par_nom ? ` par ${demande.traitee_par_nom}` : ''}
              {demande.motif_refus ? ` — ${demande.motif_refus}` : ''}
              {demande.personne_ref && (
                <>
                  {' · '}
                  <Link to={`/adherents/${demande.personne_ref}`} className="font-medium underline" onClick={basculerSurLAnnee}>
                    Ouvrir la fiche
                  </Link>
                  {anneeDeLaDemande && anneeDeLaDemande.id !== annee.anneeId && (
                    <span className="block text-xs text-encre-500">
                      L’inscription est en {demande.annee_libelle} : le bandeau y basculera.
                    </span>
                  )}
                </>
              )}
            </p>
          )}

          {/* Le choix de la fiche, puis le tableau. Quand le rapprochement
              n'a rien trouve, il n'y a rien a choisir : nouvelle fiche. */}
          {peutTraiter && demande.statut === 'recue' && !refusEnCours && r && r.personnes.length > 0 && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="fiche" checked={personneChoisie === ''} onChange={() => setPersonneChoisie('')} />
                <span>Créer une nouvelle fiche</span>
              </label>
              {r.personnes.map((p) => (
                <label key={p.id} className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="fiche"
                    checked={personneChoisie === p.id}
                    onChange={() => {
                      setPersonneChoisie(p.id);
                      setDecisions(DECISIONS_DEFAUT);
                    }}
                  />
                  <span>
                    Rattacher à {p.nom.toUpperCase()} {p.prenom}
                    {p.date_naissance ? `, née le ${formaterDate(p.date_naissance)}` : ''}
                  </span>
                </label>
              ))}
            </div>
          )}

          <TableauDemande
            demande={demande}
            personneId={peutTraiter && demande.statut === 'recue' ? personneChoisie : ''}
            decisions={decisions}
            onDecisions={setDecisions}
          />

          {peutTraiter && (
            <div className="space-y-2">
              {demande.statut === 'recue' && !refusEnCours && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="bouton-principal" disabled={enCours} onClick={() => void accepter()}>
                      {personneChoisie ? 'Rattacher et inscrire' : 'Créer l’adhérent et l’inscription'}
                    </button>
                    <button type="button" className="bouton-danger" disabled={enCours} onClick={() => setRefusEnCours(true)}>
                      Refuser…
                    </button>
                  </div>
                  <p className="text-xs text-encre-500">
                    L’inscription est créée au tarif de l’activité ; un tarif réduit se pose ensuite
                    sur la fiche. Inscription déjà saisie à la main ?{' '}
                    <button type="button" className="underline" disabled={enCours} onClick={() => void changerStatut('traitee')}>
                      Marquer traitée sans rien créer
                    </button>
                    .
                  </p>
                </div>
              )}
              {demande.statut === 'recue' && refusEnCours && (
                <div className="space-y-2">
                  <label className="block">
                    <span className="etiquette">Motif du refus</span>
                    <input className="champ" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Créneau complet, doublon…" />
                  </label>
                  <div className="flex gap-2">
                    <button type="button" className="bouton-danger" disabled={enCours || !motif.trim()} onClick={() => void changerStatut('refusee')}>
                      Confirmer le refus
                    </button>
                    <button type="button" className="bouton-secondaire" onClick={() => setRefusEnCours(false)}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
              {(demande.statut === 'traitee' || demande.statut === 'refusee') && (
                <button type="button" className="bouton-secondaire" disabled={enCours} onClick={() => void changerStatut('recue')}>
                  Remettre en attente
                </button>
              )}
              <label className="block">
                <span className="etiquette">Note interne <span className="font-normal text-encre-400">facultatif</span></span>
                <textarea className="champ min-h-[4rem]" value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => void enregistrerNote()} placeholder="Inscription saisie le … ; rappelée pour le règlement…" />
              </label>
            </div>
          )}
          {erreur && <p className="text-sm text-red-700">{erreur}</p>}
        </div>

        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-encre-500">Historique</h3>
          <div className="space-y-0.5 text-xs text-encre-500">
            <div>
              {formaterDateHeure(demande.created_at)} · reçue{demande.ip ? ` depuis ${demande.ip}` : ''}
            </div>
            {r && <div>rapprochement : {libelle.texte}</div>}
            {demande.traitee_le && (
              <div>
                {formaterDateHeure(demande.traitee_le)} · {STATUTS[demande.statut].libelle}
                {demande.traitee_par_nom ? ` par ${demande.traitee_par_nom}` : ''}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modale>
  );
}
